import { useMemo } from 'react';
import {
  QUERY_KEY_USER_EXPERIMENTS,
  QUERY_KEY_USER_INFO,
  QUERY_KEY_USER_FEATURE_CONFIGS,
} from '@wwe-portal/web/utils/constants/keys';
import {
  getUserExperiments,
  getUserInfo,
  getUserFeatureConfigs,
} from '@wwe-portal/web/services/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useGetCurrentUserId,
  useIsAuthenticated,
  useIsAuthenticating,
} from '../stores/auth-store';
import { UserData } from '@wwe-portal/entities/user.type';
import { staticAbTesting, useLocalConfig } from '../stores/local-config';

const formatKey = (key: string) => {
  const postfix = process.env.NEXT_PUBLIC_EXPERIMENT_POSTFIX;
  if (!postfix) {
    return key;
  }
  return key.endsWith(postfix) ? key.slice(0, -postfix.length) : key;
};

export const useGetUserInfo = () => {
  const userId = useGetCurrentUserId();
  const isAuthenticated = useIsAuthenticated();
  return useQuery({
    queryKey: [QUERY_KEY_USER_INFO, userId],
    queryFn: async () => {
      const response = await getUserInfo();
      return response.data.data;
    },
    enabled: isAuthenticated,
    // Add shorter stale time for energy-related data to ensure fresh updates
    staleTime: 1000, // 1 second
    // Add refetch interval to keep energy data fresh
    refetchInterval: (query) => {
      const data = query.state.data;
      // If user has energy refresh date, refetch more frequently
      if (data?.energyRefreshDate) {
        const now = Date.now();
        const refreshTime = new Date(data.energyRefreshDate).getTime();
        const timeUntilRefresh = refreshTime - now;

        // If refresh is within the next 5 minutes, refetch every 30 seconds
        if (timeUntilRefresh > 0 && timeUntilRefresh <= 5 * 60 * 1000) {
          return 30000; // 30 seconds
        }
      }
      return false; // Don't auto-refetch otherwise
    },
  });
};

export const useGetCurrentUser = () => {
  const { data } = useGetUserInfo();
  return data;
};

export const useInvalidateCurrentUser = () => {
  const queryClient = useQueryClient();
  const userId = useGetCurrentUserId();

  return async () => {
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_USER_INFO],
    });
    return queryClient.getQueryData<UserData>([QUERY_KEY_USER_INFO, userId]);
  };
};

export const useGetUserBalance = () => {
  const { data } = useGetUserInfo();
  return {
    rubies: data?.ruby,
    webCoins: data?.webCoin,
    clubCoins: data?.clubCoin,
    pick: data?.pick,
    energy: data?.energy,
  };
};

const parseExperimentValue = (experimentValue: unknown) => {
  if (typeof experimentValue === 'string') {
    return experimentValue.toLowerCase() === 'true';
  }
  if (typeof experimentValue === 'boolean') {
    return experimentValue;
  }
  return Boolean(experimentValue);
};

export const useGetUserExperiments = () => {
  const userId = useGetCurrentUserId();
  const isAuthenticated = useIsAuthenticated();
  return useQuery({
    queryKey: [QUERY_KEY_USER_EXPERIMENTS, { userId, isAuthenticated }],
    queryFn: async () => {
      const response = await getUserExperiments();
      return Object.fromEntries(
        Object.entries(response.data.data)
          .map(([key, value]) => [formatKey(key), parseExperimentValue(value)])
          .filter(([, value]) => value)
      );
    },
    enabled: isAuthenticated,
  });
};

export const useGetUserFeatureConfigs = () => {
  const userId = useGetCurrentUserId();
  const isAuthenticated = useIsAuthenticated();
  return useQuery({
    queryKey: [QUERY_KEY_USER_FEATURE_CONFIGS, { userId, isAuthenticated }],
    queryFn: async () => {
      const response = await getUserFeatureConfigs();
      return Object.fromEntries(
        Object.entries(response.data.data).map(([key, value]) => [
          formatKey(key),
          value,
        ])
      );
    },
    enabled: isAuthenticated,
  });
};

export const useFeatureConfig = <T = Record<string, unknown>>(
  featureId: string
): { isLoading: boolean; config: T | undefined } => {
  const isAuthenticated = useIsAuthenticated();
  const isAuthenticating = useIsAuthenticating();
  const { data: userFeatureConfigs, isLoading: isLoadingFeatureConfigs } =
    useGetUserFeatureConfigs();
  const localFeatureConfigs = useLocalConfig(
    (state) => state.featureConfigs || {}
  );

  const isLoading = isAuthenticating || isLoadingFeatureConfigs;

  if (!isAuthenticated || isLoading) {
    return { isLoading, config: undefined };
  }

  // Local override takes priority over server config
  const localConfig = localFeatureConfigs[featureId];
  const serverConfig = userFeatureConfigs?.[featureId];
  const config = localConfig !== undefined ? localConfig : serverConfig;

  // Return undefined for empty configs (consistent with plan)
  const hasConfig =
    config && typeof config === 'object' && Object.keys(config).length > 0;

  return {
    isLoading,
    config: hasConfig ? (config as T) : undefined,
  };
};

export const useDripFeatureConfigs = (hasDripOffersInCatalog: boolean) => {
  const isAuthenticated = useIsAuthenticated();
  const { data: userFeatureConfigs, isLoading: isLoadingFeatureConfigs } =
    useGetUserFeatureConfigs();
  const localFeatureConfigs = useLocalConfig(
    (state) => state.featureConfigs || {}
  );

  const enabled = isAuthenticated && hasDripOffersInCatalog;

  const pinnedDripByDays = useMemo(() => {
    if (!enabled) return new Map();

    const map = new Map<number, string>();

    // Check each drip duration
    const dripDurations = [7, 14, 30] as const;

    for (const duration of dripDurations) {
      const featureKey = `feature_drip_${duration}d`;

      // Local override takes priority
      const localConfig = localFeatureConfigs[featureKey];
      const serverConfig = userFeatureConfigs?.[featureKey];
      const config = localConfig !== undefined ? localConfig : serverConfig;

      // Only pin if config has offer_id
      if (
        config &&
        typeof config === 'object' &&
        'offer_id' in config &&
        config.offer_id
      ) {
        map.set(duration, config.offer_id as string);
      }
    }

    return map;
  }, [enabled, userFeatureConfigs, localFeatureConfigs]);

  return {
    pinnedDripByDays,
    isLoading: isLoadingFeatureConfigs,
  };
};

export interface UseUserExperimentOptions {
  allowAnonymous: boolean;
}

const defaultOptions: UseUserExperimentOptions = {
  allowAnonymous: false,
};

export const useUserExperiment = (
  feature: string,
  overrideOptions?: Partial<UseUserExperimentOptions>
) => {
  const options = {
    ...defaultOptions,
    ...overrideOptions,
  };
  const isAuthenticated = useIsAuthenticated();
  const isAuthenticating = useIsAuthenticating();
  const { data: userExperiments, isLoading: isLoadingExperiments } =
    useGetUserExperiments();
  const abTestingLocal = useLocalConfig((state) => state.abTesting);

  const isLoading = isAuthenticating || isLoadingExperiments;
  if (isLoading) {
    return {
      isLoading: true,
      isEnabled: false,
    };
  }

  const getIsEnabled = () => {
    // If the feature is enabled in the local config, return true
    if (abTestingLocal[feature]) {
      return true;
    }

    // If the user is not authenticated and the allowAnonymous option is true, return true
    if (!isAuthenticated && options?.allowAnonymous) {
      return true;
    }

    // If the user is not authenticated, return false
    if (!isAuthenticated) {
      return false;
    }

    return Boolean(userExperiments?.[feature] ?? false);
  };

  return {
    isLoading,
    isEnabled: getIsEnabled(),
  };
};

export const useCurrentAbTesting = () => {
  const { data: config, isLoading } = useGetUserExperiments();
  const localConfig = useLocalConfig((state) => state.abTesting);
  return {
    isLoading,
    finalConfig: isLoading
      ? null
      : {
          ...staticAbTesting,
          ...config,
          ...localConfig,
        },
    originConfig: config,
  };
};
